import React, { useEffect, useState } from 'react';
import { Button, Drawer, Field, Input, Select, Table, Checkbox, Checklist } from '@itds/elements';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import Api from '../../../../utils/Api';
import { FEE_TYPES as feeTypes, STATES } from '@itds/Constants';
import PropTypes from 'prop-types';

function FeesAndExpenses({ version }) {
  const params = useParams();
  const [feeExpense, setFeeExpense] = useState(null);
  const [feesAndExpenses, setFeesAndExpenses] = useState([]);
  const [bands, setBands] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProductType, setSelectedProductType] = useState('');
  const [selectedFeeType, setSelectedFeeType] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    async function getFeesAndExpenses() {
      if (!version?.id) return;

      try {
        const response = await Api.loadLifeFeesAndExpenses(version.id);

        setFeesAndExpenses(response?.data || []);
      } catch (error) {
        console.error('Error loading fees and expenses:', error);
      }
    }
    getFeesAndExpenses();
  }, [version?.id]);

  // Load IUL bands and products when component mounts
  useEffect(() => {
    async function loadBandsAndProducts() {
      if (!params.id || !params.version_id) return;
      try {
        const apiParams = {
          carrier_id: params.id,
          data_version_id: params.version_id
        };

        // NOTE: Only show bands with IUL product type as per requirements
        const bandsResponse = await Api.loadBands({
          ...apiParams,
          product_type: 'IUL'
        });
        const productsResponse = await Api.loadProducts(apiParams);

        setBands(bandsResponse?.data || []);
        setProducts(productsResponse?.data || []);
      } catch (error) {
        console.error('Error loading bands and products:', error);
        setBands([]);
        setProducts([]);
      }
    }
    loadBandsAndProducts();
  }, [params.id, params.version_id]);
  // Load bands when drawer opens (only IUL bands as per requirements)
  useEffect(() => {
    async function loadBandsOnDrawerOpen() {
      if (!isDrawerOpen || !feeExpense?.product_type || !params.id || !params.version_id) return;
      try {
        // NOTE: Only show bands with IUL product type as per requirements
        const response = await Api.loadBands({
          carrier_id: params.id,
          data_version_id: params.version_id,
          product_type: 'IUL'
        });

        setBands(response?.data || []);
      } catch (error) {
        console.error('Error loading bands on drawer open:', error);
        setBands([]);
      }
    }
    loadBandsOnDrawerOpen();
  }, [isDrawerOpen, feeExpense?.product_type, params.id, params.version_id]);

  function getValue(eOrVal) {
    return eOrVal?.target?.value ?? eOrVal?.value ?? eOrVal;
  }

  function handleInputChange(name) {
    return function onChange(eOrVal) {
      const v = getValue(eOrVal);

      setFeeExpense(prev => ({ ...prev, [name]: v }));
    };
  }

  function handleSave() {
    // Helper function to process effective policy rates with proper year range format
    const processEffectivePolicyRates = rates => {
      if (!Array.isArray(rates)) return [];

      return rates.map(rate => {
        const startYear = rate.effective_year_start
          ? rate.effective_year_start.toString().trim()
          : '';
        const endYear = rate.effective_year_end ? rate.effective_year_end.toString().trim() : '';

        // Only create year range if we have at least a start year
        let effectiveYearRange = '';

        if (startYear) {
          effectiveYearRange = endYear ? `${startYear}-${endYear}` : startYear;
        }

        const result = {
          effective_year_range: effectiveYearRange,
          updated_rate: parseFloat(rate.updated_rate) || 0,
          updated_rate_years: parseInt(rate.updated_rate_years, 10) || 0
        };

        return result;
      });
    };

    const payload = {
      product_type: feeExpense?.product_type?.toUpperCase(),
      product_type_detail: feeExpense?.product_type_detail,
      carrier_id: parseInt(params.id, 10),
      data_version_id: parseInt(version?.id, 10),
      fund_type: 'Fixed', // Always Fixed as per schema
      fee_type: feeExpense?.fee_type,
      state_availability: Array.isArray(feeExpense?.state_availability)
        ? feeExpense.state_availability
        : [],
      current_rate: parseFloat(feeExpense?.current_rate) || 0,
      current_rate_years: parseInt(feeExpense?.current_rate_years, 10) || 0,
      rate_threshold: parseFloat(feeExpense?.rate_after_threshold) || 0,
      rate_years_threshold: parseInt(feeExpense?.rate_after_threshold_years, 10) || 0,
      subsequent_rate: parseFloat(feeExpense?.rate_after_threshold) || 0, // Same as rate_threshold
      subsequent_rate_years: parseInt(feeExpense?.rate_after_threshold_years, 10) || 0, // Same as rate_years_threshold
      effective_policy_rates: processEffectivePolicyRates(feeExpense?.effective_policy_rates),
      band_ids: Array.isArray(feeExpense?.band_ids) ? feeExpense.band_ids : [],
      product_ids: Array.isArray(feeExpense?.product_ids) ? feeExpense.product_ids : []
    };

    // Add ID to payload when editing
    if (feeExpense?.id) {
      payload.id = feeExpense.id;
    }

    if (!feeExpense?.id) {
      Api.saveNewLifeFeeExpense(payload)
        .then(() => {
          setIsDrawerOpen(false);
          loadFeesAndExpenses();
        })
        .catch(error => {
          console.error('Error saving fee/expense:', error);
        });
    } else {
      Api.saveEditLifeFeeExpense(payload)
        .then(() => {
          setIsDrawerOpen(false);
          loadFeesAndExpenses();
        })
        .catch(error => {
          console.error('Error saving fee/expense:', error);
        });
    }
  }
  function loadFeesAndExpenses() {
    if (!version?.id) return;

    Api.loadLifeFeesAndExpenses(version.id)
      .then(response => {
        setFeesAndExpenses(response?.data || []);
      })
      .catch(error => {
        console.error('Error loading fees and expenses:', error);
      });
  }
  function openCreateNewDrawer() {
    setFeeExpense({
      data_version_id: version?.id,
      carrier_id: params.id,
      product_type: '',
      product_type_detail: '',
      fee_type: '', // Don't default to PremiumLoad, let user select
      state_availability: [],
      current_rate: '',
      current_rate_years: '',
      rate_after_threshold: '',
      rate_after_threshold_years: '',
      effective_policy_rates: [],
      band_ids: [],
      product_ids: []
    });
    setIsDrawerOpen(true);
  }

  function closeDrawer() {
    setIsDrawerOpen(false);
  }
  function onRowClick(row) {
    // Helper function to parse effective year ranges back to start and end
    const parseEffectivePolicyRates = rates => {
      if (!Array.isArray(rates)) return [];

      return rates.map(rate => {
        let start = '';
        let end = '';

        // First, check if the rate already has start/end fields (in case API returns them directly)
        if ('effective_year_start' in rate || 'effective_year_end' in rate) {
          start = rate.effective_year_start || '';
          end = rate.effective_year_end || '';
        } else if (rate.effective_year_range) {
          // Parse from the year range string
          const effectiveYearRange = rate.effective_year_range.toString();

          if (effectiveYearRange.includes('-')) {
            const [rangeStart, rangeEnd] = effectiveYearRange.split('-');

            start = rangeStart ? rangeStart.trim() : '';
            end = rangeEnd ? rangeEnd.trim() : '';
          } else if (effectiveYearRange) {
            // If no dash, treat as start year only
            start = effectiveYearRange.trim();
          }
        }

        const result = {
          effective_year_start: start,
          effective_year_end: end,
          updated_rate: rate.updated_rate || '',
          updated_rate_years: rate.updated_rate_years || ''
        };

        return result;
      });
    };

    setFeeExpense({
      ...row,
      // Map API response fields to form fields
      rate_after_threshold: row.rate_threshold,
      rate_after_threshold_years: row.rate_years_threshold,
      effective_policy_rates: parseEffectivePolicyRates(row.effective_policy_rates),
      product_ids: row.product_ids || []
    });
    setIsDrawerOpen(true);
  }
  function getFilteredFeesAndExpenses() {
    let filtered = feesAndExpenses;

    if (selectedProductType) {
      filtered = filtered.filter(fee => fee.product_type === selectedProductType);
    }

    if (selectedFeeType) {
      filtered = filtered.filter(fee => fee.fee_type === selectedFeeType);
    }

    return filtered;
  }
  function getColumns() {
    return [
      {
        id: 'fee_type',
        label: 'Fee Type',
        width: 120,
        render: row => row.fee_type || 'N/A'
      },
      {
        id: 'product_type',
        label: 'Product Type',
        width: 100,
        render: row => row.product_type || 'N/A'
      },
      // {
      //   id: 'current_rate',
      //   label: 'Current Rate',
      //   render: row => (row.current_rate ? `${row.current_rate}%` : 'N/A')
      // },
      {
        id: 'bands',
        label: 'Bands',
        width: 120,
        render: row => {
          if (Array.isArray(row.band_names)) {
            return (
              <div
                style={{
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  width: '120px',
                  lineHeight: '1.3',
                  fontSize: '12px'
                }}
              >
                {row.band_names.join(', ')}
              </div>
            );
          }
          return row.band || row.bands || 'N/A';
        }
      },
      {
        id: 'state_availability',
        label: 'States',
        width: 150, // Set fixed width for the column
        render: row => {
          if (Array.isArray(row.state_availability)) {
            const states = row.state_availability;

            // Always show the actual state codes with proper wrapping
            return (
              <div
                style={{
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  width: '150px',
                  lineHeight: '1.3',
                  fontSize: '12px'
                }}
              >
                {states.join(', ')}
              </div>
            );
          }
          return row.state || row.state_availability || 'N/A';
        }
      },
      {
        id: 'updated_at',
        label: 'Date Updated',
        width: 100,
        render: row => (row.updated_at ? format(new Date(row.updated_at), 'MM/dd/yyyy') : 'N/A')
      }
    ];
  }

  const filteredFeesAndExpenses = getFilteredFeesAndExpenses();
  const columns = getColumns();

  function addAdditionalLine() {
    const currentRates = Array.isArray(feeExpense?.effective_policy_rates)
      ? feeExpense.effective_policy_rates
      : [];

    const newLine = {
      effective_year_start: '',
      effective_year_end: '',
      updated_rate: '',
      updated_rate_years: ''
    };

    setFeeExpense(prev => ({
      ...prev,
      effective_policy_rates: [...currentRates, newLine]
    }));
  }

  function updateAdditionalLine(index, field, value) {
    const currentRates = Array.isArray(feeExpense?.effective_policy_rates)
      ? [...feeExpense.effective_policy_rates]
      : [];

    if (currentRates[index]) {
      currentRates[index] = {
        ...currentRates[index],
        [field]: value
      };

      setFeeExpense(prev => ({
        ...prev,
        effective_policy_rates: currentRates
      }));
    }
  }

  function removeAdditionalLine(index) {
    const currentRates = Array.isArray(feeExpense?.effective_policy_rates)
      ? [...feeExpense.effective_policy_rates]
      : [];

    currentRates.splice(index, 1);

    setFeeExpense(prev => ({
      ...prev,
      effective_policy_rates: currentRates
    }));
  }

  const effectivePolicyRates = Array.isArray(feeExpense?.effective_policy_rates)
    ? feeExpense.effective_policy_rates
    : [];

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 30
        }}
      >
        {' '}
        <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>Product Type Filter:</label>
            <Select
              onChange={e => setSelectedProductType(getValue(e))}
              options={[
                { value: '', label: 'All Product Types' },
                { value: 'IUL', label: 'IUL' },
                { value: 'VUL', label: 'VUL' }
              ]}
              style={{ minWidth: 200 }}
              value={selectedProductType}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>Fee Type Filter:</label>
            <Select
              onChange={e => setSelectedFeeType(getValue(e))}
              options={[
                { value: '', label: 'All Fee Types' },
                ...Object.keys(feeTypes).map(key => ({
                  value: key,
                  label: feeTypes[key]
                }))
              ]}
              style={{ minWidth: 200 }}
              value={selectedFeeType}
            />
          </div>
        </div>{' '}
        <Button onClick={openCreateNewDrawer} style={{}}>
          Add New
        </Button>
      </div>{' '}
      <Table columns={columns} data={filteredFeesAndExpenses} onRowClick={onRowClick} />
      <Drawer
        alignRight={true}
        footer={
          <Button onClick={handleSave} style={{}}>
            {feeExpense?.id ? 'Update Fee/Expense' : 'Create Fee/Expense'}
          </Button>
        }
        onClose={closeDrawer}
        show={isDrawerOpen}
        title={feeExpense?.id ? 'Edit Record' : 'Record Editor'}
      >
        {' '}
        <Field label='Type of Fee' style={{}}>
          <Select
            onChange={handleInputChange('fee_type')}
            options={[
              { value: '', label: 'Select Fee Type' },
              ...Object.keys(feeTypes).map(key => ({
                value: key,
                label: feeTypes[key]
              }))
            ]}
            style={{}}
            value={feeExpense?.fee_type || ''}
          />
        </Field>{' '}
        <Field label='Product Type' style={{}}>
          <Select
            onChange={eOrVal => {
              const value = getValue(eOrVal);

              handleInputChange('product_type')(value); // Load bands for the new product type
              if (value && params.id && params.version_id) {
                // NOTE: Only show bands with IUL product type as per requirements
                Api.loadBands({
                  carrier_id: params.id,
                  data_version_id: params.version_id,
                  product_type: 'IUL'
                })
                  .then(response => {
                    setBands(response?.data || []);
                  })
                  .catch(error => {
                    console.error('Error loading bands for product type change:', error);
                    setBands([]);
                  });
              } else {
                setBands([]);
              }
            }}
            options={[
              { value: '', label: 'Select Product Type' },
              { value: 'IUL', label: 'IUL' },
              { value: 'VUL', label: 'VUL' }
            ]}
            style={{}}
            value={feeExpense?.product_type || ''}
          />
        </Field>{' '}
        <Field label='Product Type Detail' style={{}}>
          <Input
            onChange={handleInputChange('product_type_detail')}
            placeholder='e.g., To age 70'
            value={feeExpense?.product_type_detail || ''}
          />{' '}
        </Field>{' '}
        {/* Show Bands field only when product type is NOT VUL (always loads IUL bands) */}
        {feeExpense?.product_type && feeExpense?.product_type !== 'VUL' && (
          <Field label='Bands (IUL Product Type Only)' style={{}}>
            <div
              style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #ccc', padding: 10 }}
            >
              {bands.length === 0 ? (
                <div>No Available Bands for IUL</div>
              ) : (
                bands.map(band => (
                  <div key={band.id} style={{ marginBottom: 8 }}>
                    <Checkbox
                      checked={
                        Array.isArray(feeExpense?.band_ids)
                          ? feeExpense.band_ids.includes(band.id)
                          : false
                      }
                      label={`${band.band_description} (${band.min_band_value || 0} - ${band.max_band_value || 'Unlimited'})`}
                      onChange={e => {
                        const isChecked = e.target.checked;

                        const currentBands = Array.isArray(feeExpense?.band_ids)
                          ? feeExpense.band_ids
                          : [];

                        if (isChecked) {
                          handleInputChange('band_ids')([...currentBands, band.id]);
                        } else {
                          handleInputChange('band_ids')(currentBands.filter(id => id !== band.id));
                        }
                      }}
                    />
                  </div>
                ))
              )}
            </div>
          </Field>
        )}{' '}
        <Field label='Products' style={{}}>
          <Checklist
            columns={3}
            onChange={selected => handleInputChange('product_ids')(selected)}
            options={products.map(p => ({ value: p.id, label: p.product_name }))}
            selected={Array.isArray(feeExpense?.product_ids) ? feeExpense.product_ids : []}
            showSelectAll={true}
            style={{}}
          />
        </Field>{' '}
        <Field label='State Availability' style={{}}>
          <div style={{ marginBottom: 10 }}>
            <Checkbox
              checked={
                Array.isArray(feeExpense?.state_availability) &&
                feeExpense.state_availability.length === STATES.length &&
                STATES.every(state => feeExpense.state_availability.includes(state.value))
              }
              label='All States'
              onChange={e => {
                const isChecked = e.target.checked;

                if (isChecked) {
                  // When "All States" is checked, populate with all state codes
                  const allStateCodes = STATES.map(state => state.value);

                  handleInputChange('state_availability')(allStateCodes);
                } else {
                  handleInputChange('state_availability')([]);
                }
              }}
            />
          </div>
          {!(
            Array.isArray(feeExpense?.state_availability) &&
            feeExpense.state_availability.length === STATES.length &&
            STATES.every(state => feeExpense.state_availability.includes(state.value))
          ) && (
            <div
              style={{
                maxHeight: 200,
                overflowY: 'auto',
                border: '1px solid #ccc',
                padding: 10
              }}
            >
              {STATES.map(state => (
                <div key={state.value} style={{ marginBottom: 5 }}>
                  <Checkbox
                    checked={
                      Array.isArray(feeExpense?.state_availability)
                        ? feeExpense.state_availability.includes(state.value)
                        : false
                    }
                    label={`${state.label} (${state.value})`}
                    onChange={e => {
                      const isChecked = e.target.checked;
                      const currentStates = Array.isArray(feeExpense?.state_availability)
                        ? feeExpense.state_availability
                        : [];

                      if (isChecked) {
                        handleInputChange('state_availability')([...currentStates, state.value]);
                      } else {
                        handleInputChange('state_availability')(
                          currentStates.filter(s => s !== state.value)
                        );
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </Field>{' '}
        {/* Show these fields only for Premium Load */}
        {feeExpense?.fee_type === 'PremiumLoad' && (
          <>
            <Field label='Current Rate (%)' style={{}}>
              <Input
                onChange={handleInputChange('current_rate')}
                placeholder='Enter current rate'
                step='0.01'
                type='number'
                value={feeExpense?.current_rate || ''}
              />
            </Field>
            <Field label='Current Rate Years (# of Years)' style={{}}>
              <Input
                onChange={handleInputChange('current_rate_years')}
                placeholder='Enter years for current rate'
                type='number'
                value={feeExpense?.current_rate_years || ''}
              />
            </Field>
            <Field label='Rate After Threshold (%)' style={{}}>
              <Input
                onChange={handleInputChange('rate_after_threshold')}
                placeholder='Enter rate after threshold'
                step='0.01'
                type='number'
                value={feeExpense?.rate_after_threshold || ''}
              />
            </Field>
            <Field label='Rate After Threshold Years (# of Years)' style={{}}>
              <Input
                onChange={handleInputChange('rate_after_threshold_years')}
                placeholder='Enter years for rate after threshold'
                type='number'
                value={feeExpense?.rate_after_threshold_years || ''}
              />
            </Field>
          </>
        )}{' '}
        {/* Show fields only for Expense Charge */}
        {feeExpense?.fee_type === 'ExpenseCharge' && (
          <>
            <Field label='Current Rate (%/$) per thousand' style={{}}>
              <Input
                onChange={handleInputChange('current_rate')}
                placeholder='Enter current rate per thousand'
                step='0.01'
                type='number'
                value={feeExpense?.current_rate || ''}
              />
            </Field>
            <Field label='Current Rate Years (# of Years)' style={{}}>
              <Input
                onChange={handleInputChange('current_rate_years')}
                placeholder='Enter years for current rate'
                type='number'
                value={feeExpense?.current_rate_years || ''}
              />
            </Field>
          </>
        )}
        {/* Additional Rate Lines - show for Premium Load */}
        {feeExpense?.fee_type === 'PremiumLoad' && (
          <Field label='Additional Rate Schedules' style={{}}>
            {effectivePolicyRates.length > 0 ? (
              <div>
                {effectivePolicyRates.map((rate, index) => (
                  <div
                    key={index}
                    style={{
                      border: '1px solid #ddd',
                      padding: 10,
                      marginBottom: 10,
                      borderRadius: 4
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 10,
                        marginBottom: 10
                      }}
                    >
                      {' '}
                      <div>
                        <label style={{ display: 'block', marginBottom: 5 }}>
                          Effective Year Range (Start):
                        </label>
                        <Input
                          onChange={e =>
                            updateAdditionalLine(index, 'effective_year_start', getValue(e))
                          }
                          placeholder='e.g., 3'
                          type='number'
                          value={rate.effective_year_start || ''}
                        />
                      </div>{' '}
                      <div>
                        <label style={{ display: 'block', marginBottom: 5 }}>
                          Effective Year Range (End):
                        </label>
                        <Input
                          onChange={e =>
                            updateAdditionalLine(index, 'effective_year_end', getValue(e))
                          }
                          placeholder='e.g., 10'
                          type='number'
                          value={rate.effective_year_end || ''}
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 10,
                        marginBottom: 10
                      }}
                    >
                      <div>
                        <label style={{ display: 'block', marginBottom: 5 }}>
                          Updated Rate (%):
                        </label>
                        <Input
                          onChange={e => updateAdditionalLine(index, 'updated_rate', getValue(e))}
                          placeholder='Enter updated rate'
                          step='0.01'
                          type='number'
                          value={rate.updated_rate || ''}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 5 }}>
                          Updated Rate Years:
                        </label>
                        <Input
                          onChange={e =>
                            updateAdditionalLine(index, 'updated_rate_years', getValue(e))
                          }
                          placeholder='Enter years'
                          type='number'
                          value={rate.updated_rate_years || ''}
                        />
                      </div>
                    </div>
                    <Button
                      onClick={() => removeAdditionalLine(index)}
                      style={{
                        marginTop: 10,
                        backgroundColor: '#dc3545',
                        color: 'white'
                      }}
                    >
                      Remove Line{' '}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '10px', color: '#666', fontStyle: 'italic' }}>
                No additional rate schedules yet. Click "Add Additional Line" below to add one.
              </div>
            )}
          </Field>
        )}
        {/* Additional Rate Lines - show for Expense Charge */}
        {feeExpense?.fee_type === 'ExpenseCharge' && (
          <Field label='Additional Rate Schedules' style={{}}>
            {effectivePolicyRates.length > 0 ? (
              <div>
                {effectivePolicyRates.map((rate, index) => (
                  <div
                    key={index}
                    style={{
                      border: '1px solid #ddd',
                      padding: 10,
                      marginBottom: 10,
                      borderRadius: 4
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 10,
                        marginBottom: 10
                      }}
                    >
                      {' '}
                      <div>
                        <label style={{ display: 'block', marginBottom: 5 }}>
                          Effective Year Range (Start):
                        </label>
                        <Input
                          onChange={e =>
                            updateAdditionalLine(index, 'effective_year_start', getValue(e))
                          }
                          placeholder='e.g., 3'
                          type='number'
                          value={rate.effective_year_start || ''}
                        />
                      </div>{' '}
                      <div>
                        <label style={{ display: 'block', marginBottom: 5 }}>
                          Effective Year Range (End):
                        </label>
                        <Input
                          onChange={e =>
                            updateAdditionalLine(index, 'effective_year_end', getValue(e))
                          }
                          placeholder='e.g., 10'
                          type='number'
                          value={rate.effective_year_end || ''}
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 10,
                        marginBottom: 10
                      }}
                    >
                      <div>
                        <label style={{ display: 'block', marginBottom: 5 }}>
                          Updated Rate (%):
                        </label>
                        <Input
                          onChange={e => updateAdditionalLine(index, 'updated_rate', getValue(e))}
                          placeholder='Enter updated rate '
                          step='0.01'
                          type='number'
                          value={rate.updated_rate || ''}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 5 }}>
                          Updated Rate Years:
                        </label>
                        <Input
                          onChange={e =>
                            updateAdditionalLine(index, 'updated_rate_years', getValue(e))
                          }
                          placeholder='Enter years'
                          type='number'
                          value={rate.updated_rate_years || ''}
                        />
                      </div>
                    </div>
                    <Button
                      onClick={() => removeAdditionalLine(index)}
                      style={{
                        marginTop: 10,
                        backgroundColor: '#dc3545',
                        color: 'white'
                      }}
                    >
                      Remove Line{' '}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '10px', color: '#666', fontStyle: 'italic' }}>
                No additional rate schedules yet. Click "Add Additional Line" below to add one.
              </div>
            )}
          </Field>
        )}{' '}
        {(feeExpense?.fee_type === 'PremiumLoad' || feeExpense?.fee_type === 'ExpenseCharge') && (
          <Button onClick={addAdditionalLine} style={{ marginTop: 10 }}>
            Add Additional Line
          </Button>
        )}
      </Drawer>
    </>
  );
}

FeesAndExpenses.propTypes = {
  version: PropTypes.object
};

export default FeesAndExpenses;
